import NavResponsiveModal, {
    ModalActions,
    ModalContent,
    ModalHeader,
} from '@/components/Navigation/NavResponsiveModal';
import { PrimaryButton, SecondaryButton } from '@/styles/blocks';
import { Grid } from '@material-ui/core';
import React, { useEffect, useState, useMemo } from 'react';
import { css } from '@emotion/react';
import { OnwardCheckbox } from '@/components/ShipmentForm/blocks';
import { StickyModalActions } from '@/styles/blocks';
import { captureException } from '@sentry/react';
import { colors } from '@/styles';
import { collectPrintSelection, buildMergedLabelsHtmlAsync } from '@onward-delivery/core';
import { alignManifestForItemLabelPrinting } from '@/utilities/enrichManifest';
import { buildGroupedManifestItemsAndCounts } from '@/utilities/manifestPrintGrouping';
import { printHtmlLabelsViaIframe } from '@/utilities/printManifestLabels';

const labelRow = (key, item, handleCheck, handleItemCheck, isFirst) => {
    // Extract order information
    const orderInfo = item.isPallet && item.items && item.items[0]?.order ? item.items[0].order : item.items?.order;

    const orderNumber = orderInfo?.order_number || '—';
    const poNumber = orderInfo?.po_number || '—';
    const itemCount = item.isPallet ? item.items.length : 1;

    // Calculate total weight/dimensions if available
    const totalWeight = item.isPallet
        ? item.items.reduce((sum, i) => sum + (parseFloat(i.weight) || 0), 0).toFixed(2)
        : parseFloat(item.items?.weight || 0).toFixed(2);

    return (
        <Grid
            container
            direction="row"
            css={css`
                flex-wrap: nowrap;
                align-items: flex-start;
                ${!isFirst ? `border-top: 1px solid ${colors.greys.border};` : ''}
                padding: 16px 0;
                width: 100%;
                transition: all 0.15s ease;
                border-left: 3px solid transparent;
                &:hover {
                    background-color: ${colors.greys.lightest};
                    border-left: 3px solid #0072ce;
                }
            `}
        >
            <Grid
                item
                css={css`
                    padding-top: 4px;
                    padding-right: 12px;
                `}
            >
                <OnwardCheckbox checked={item.checked} onChange={() => handleCheck(key)} />
            </Grid>

            <Grid
                item
                container
                css={css`
                    flex-direction: column;
                    flex: 1;
                    max-width: calc(100% - 40px);
                `}
            >
                {/* Top row with item/pallet ID and weight */}
                <Grid
                    item
                    container
                    css={css`
                        justify-content: space-between;
                        align-items: center;
                        width: 100%;
                    `}
                >
                    {/* Left side - ID and item count */}
                    <Grid item>
                        <div
                            css={css`
                                display: flex;
                                align-items: center;
                            `}
                        >
                            <span
                                css={css`
                                    font-weight: 700;
                                    font-size: 16px;
                                `}
                            >
                                {key}
                            </span>

                            <span
                                css={css`
                                    background-color: #e6f7ff;
                                    border-radius: 4px;
                                    padding: 2px 8px;
                                    font-size: 13px;
                                    font-weight: 500;
                                    color: #0072ce;
                                    margin-left: 8px;
                                    flex-shrink: 0;
                                `}
                            >
                                {itemCount} {itemCount === 1 ? 'item' : 'items'}
                            </span>
                        </div>
                    </Grid>

                    {/* Right side - Weight */}
                    {totalWeight > 0 && (
                        <Grid item>
                            <span
                                css={css`
                                    background-color: #f6f6f6;
                                    border-radius: 4px;
                                    padding: 2px 8px;
                                    font-size: 13px;
                                    color: ${colors.greys.dark};
                                    flex-shrink: 0;
                                    font-weight: 500;
                                `}
                            >
                                {totalWeight} lbs
                            </span>
                        </Grid>
                    )}
                </Grid>

                {/* Order information row */}
                <Grid
                    item
                    container
                    css={css`
                        margin-top: 8px;
                        padding: 8px;
                        background-color: #f9f9f9;
                        border-radius: 4px;
                        justify-content: space-between;
                        align-items: center;
                        width: 100%;
                    `}
                >
                    <Grid item>
                        <span
                            css={css`
                                font-size: 14px;
                                font-weight: 500;
                            `}
                        >
                            Order:{' '}
                            <a
                                href="#"
                                css={css`
                                    color: #0072ce;
                                    text-decoration: none;
                                `}
                            >
                                {orderNumber}
                            </a>
                        </span>
                    </Grid>

                    <Grid item>
                        <span
                            css={css`
                                font-size: 14px;
                                font-weight: 500;
                            `}
                        >
                            PO:{' '}
                            <a
                                href="#"
                                css={css`
                                    color: #0072ce;
                                    text-decoration: none;
                                `}
                            >
                                {poNumber}
                            </a>
                        </span>
                    </Grid>
                </Grid>

                {/* Item descriptions */}
                <Grid
                    item
                    css={css`
                        margin-top: 12px;
                        width: 100%;
                    `}
                >
                    {item.isPallet ? (
                        item.items.map((palletItem, index) => (
                            <div
                                key={index}
                                css={css`
                                    display: flex;
                                    align-items: flex-start;
                                    margin-bottom: ${index < item.items.length - 1 ? '10px' : '0'};
                                    padding-bottom: ${index < item.items.length - 1 ? '10px' : '0'};
                                    ${index < item.items.length - 1 ? 'border-bottom: 1px dashed #eee;' : ''}
                                    width: 100%;
                                `}
                            >
                                <span
                                    css={css`
                                        min-width: 24px;
                                        height: 24px;
                                        border-radius: 50%;
                                        background-color: #0072ce;
                                        color: white;
                                        display: inline-flex;
                                        align-items: center;
                                        justify-content: center;
                                        font-size: 12px;
                                        margin-right: 10px;
                                        flex-shrink: 0;
                                        font-weight: 500;
                                    `}
                                >
                                    {index + 1}
                                </span>
                                <div
                                    css={css`
                                        display: flex;
                                        flex-direction: row;
                                        align-items: flex-start;
                                        flex: 1;
                                        width: 100%;
                                        min-width: 0;
                                    `}
                                >
                                    <OnwardCheckbox
                                        checked={item.itemChecks?.[index] || false}
                                        onChange={() => handleItemCheck(key, index)}
                                    />
                                    <div
                                        css={css`
                                            display: flex;
                                            flex-direction: column;
                                            margin-left: 8px;
                                            flex: 1;
                                            min-width: 0;
                                        `}
                                    >
                                        {palletItem?._printListKey ? (
                                            <div
                                                css={css`
                                                    font-weight: 700;
                                                    font-size: 14px;
                                                    color: ${colors.greys.dark};
                                                    margin-bottom: 4px;
                                                `}
                                            >
                                                {palletItem._printListKey}
                                            </div>
                                        ) : null}
                                        <div
                                            css={css`
                                                font-weight: 500;
                                                font-size: 14px;
                                            `}
                                        >
                                            {palletItem?.description || 'No description'}
                                        </div>
                                        {palletItem?.sku && (
                                            <div
                                                css={css`
                                                    color: ${colors.greys.secondary};
                                                    font-size: 13px;
                                                    margin-top: 2px;
                                                `}
                                            >
                                                SKU: {palletItem.sku}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div
                            css={css`
                                display: flex;
                                align-items: flex-start;
                                width: 100%;
                            `}
                        >
                            <span
                                css={css`
                                    min-width: 24px;
                                    height: 24px;
                                    border-radius: 50%;
                                    background-color: #0072ce;
                                    color: white;
                                    display: inline-flex;
                                    align-items: center;
                                    justify-content: center;
                                    font-size: 12px;
                                    margin-right: 10px;
                                    flex-shrink: 0;
                                    font-weight: 500;
                                `}
                            >
                                1
                            </span>
                            <div
                                css={css`
                                    display: flex;
                                    flex-direction: column;
                                    width: 100%;
                                `}
                            >
                                <div
                                    css={css`
                                        font-weight: 500;
                                        font-size: 14px;
                                    `}
                                >
                                    {item.items?.description || 'No description'}
                                </div>
                                {item.items?.sku && (
                                    <div
                                        css={css`
                                            color: ${colors.greys.secondary};
                                            font-size: 13px;
                                            margin-top: 2px;
                                        `}
                                    >
                                        SKU: {item.items.sku}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </Grid>
            </Grid>
        </Grid>
    );
};

const PrintLabelsModal = ({ printingManifest, loading, callbacks }) => {
    const [manifestItems, setManifestItems] = useState({});
    const [printing, setPrinting] = useState(false);

    const manifestId = printingManifest?.manifest_id;
    const itemKey = useMemo(
        () =>
            (printingManifest?.items || [])
                .map((i) => i.mapping_id)
                .sort()
                .join(','),
        [printingManifest]
    );

    useEffect(() => {
        if (!printingManifest) {
            setManifestItems({});
            return;
        }
        const { groupedItems } = buildGroupedManifestItemsAndCounts(
            alignManifestForItemLabelPrinting(printingManifest)
        );
        setManifestItems(groupedItems);
    }, [manifestId, itemKey]);

    const cancel = () => {
        callbacks.printLabels(null);
    };

    const printSelected = async () => {
        setPrinting(true);
        try {
            const { itemsToPrint, palletsToPrint } = collectPrintSelection(manifestItems);
            const html = await buildMergedLabelsHtmlAsync({ itemsToPrint, palletsToPrint });
            await printHtmlLabelsViaIframe(html);
        } catch (error) {
            captureException(error);
            console.error('Print labels failed', error);
        } finally {
            setPrinting(false);
        }
    };

    const anySelected = useMemo(() => {
        return Object.entries(manifestItems).some(([key, i]) => {
            if (i.checked) {
                return true;
            }
            if (i.isPallet && i.itemChecks && Object.values(i.itemChecks).some((checked) => checked)) {
                return true;
            }
            return false;
        });
    }, [manifestItems]);

    const handleCheck = (name) => {
        setManifestItems((prev) => {
            const prevObject = { ...prev };
            prevObject[name].checked = !prevObject[name].checked;
            return prevObject;
        });
    };

    const handleItemCheck = (palletKey, itemIndex) => {
        setManifestItems((prev) => {
            const prevObject = { ...prev };
            const itemChecks = { ...prevObject[palletKey].itemChecks };
            itemChecks[itemIndex] = !itemChecks[itemIndex];
            prevObject[palletKey].itemChecks = itemChecks;
            return prevObject;
        });
    };

    const handleCheckAll = () => {
        if (Object.values(manifestItems).every((i) => i.checked)) {
            const itemsClone = {
                ...manifestItems,
            };
            for (const key of Object.keys(itemsClone)) {
                itemsClone[key].checked = false;
                if (itemsClone[key].isPallet) {
                    itemsClone[key].itemChecks = {};
                }
            }
            setManifestItems(itemsClone);
        } else {
            const itemsClone = {
                ...manifestItems,
            };
            for (const key of Object.keys(itemsClone)) {
                itemsClone[key].checked = true;
                if (itemsClone[key].isPallet) {
                    // Mark all items in the pallet as checked
                    const itemChecks = {};
                    itemsClone[key].items.forEach((_, index) => {
                        itemChecks[index] = true;
                    });
                    itemsClone[key].itemChecks = itemChecks;
                }
            }
            setManifestItems(itemsClone);
        }
    };

    const handleCheckAllItems = () => {
        const itemsClone = { ...manifestItems };
        for (const key of Object.keys(itemsClone)) {
            if (itemsClone[key].isPallet) {
                const itemChecks = {};
                itemsClone[key].items.forEach((_, index) => {
                    itemChecks[index] = true;
                });
                itemsClone[key].itemChecks = itemChecks;
            } else {
                itemsClone[key].checked = true;
            }
        }
        setManifestItems(itemsClone);
    };

    const handleCheckAllPallets = () => {
        const itemsClone = { ...manifestItems };
        for (const key of Object.keys(itemsClone)) {
            if (itemsClone[key].isPallet) {
                itemsClone[key].checked = true;
            }
        }
        setManifestItems(itemsClone);
    };

    return (
        <>
            <NavResponsiveModal
                open={!!printingManifest}
                onClose={cancel}
                maxWidth="sm"
                fullWidth
                css={css`
                    .MuiDialogContent-root {
                        overflow-x: hidden;
                        padding-left: 16px;
                        padding-right: 16px;
                    }
                `}
            >
                <ModalHeader title={'Print Labels'} onClose={cancel} />
                <ModalContent
                    css={css`
                        width: 100%;
                        padding: 0 16px;
                        box-sizing: border-box;
                    `}
                >
                    <Grid
                        container
                        direction="column"
                        css={css`
                            height: 100%;
                            flex-wrap: nowrap;
                        `}
                    >
                        <Grid
                            item
                            container
                            direction="row"
                            css={css`
                                flex-wrap: nowrap;
                                align-items: center;
                                margin-bottom: 1.5rem;
                                width: 100%;
                            `}
                        >
                            <Grid item>
                                <OnwardCheckbox
                                    checked={Object.values(manifestItems).every((i) => i.checked)}
                                    onChange={handleCheckAll}
                                />
                            </Grid>

                            <Grid
                                item
                                container
                                css={css`
                                    margin-left: 15px;
                                    flex-direction: column;
                                `}
                            >
                                <Grid
                                    item
                                    css={css`
                                        font-weight: 700;
                                    `}
                                >
                                    Select All
                                </Grid>
                            </Grid>
                            <Grid
                                item
                                container
                                css={css`
                                    justify-content: flex-end;
                                    gap: 12px;
                                    flex: 1;
                                `}
                            >
                                <Grid
                                    item
                                    css={css`
                                        font-size: 13px;
                                        color: ${colors.greens.primary};
                                        cursor: pointer;
                                        white-space: nowrap;
                                        &:hover {
                                            text-decoration: underline;
                                        }
                                    `}
                                    onClick={handleCheckAllItems}
                                >
                                    Select All Items
                                </Grid>
                                <Grid
                                    item
                                    css={css`
                                        font-size: 13px;
                                        color: ${colors.greens.primary};
                                        cursor: pointer;
                                        white-space: nowrap;
                                        &:hover {
                                            text-decoration: underline;
                                        }
                                    `}
                                    onClick={handleCheckAllPallets}
                                >
                                    Select All Pallets
                                </Grid>
                            </Grid>
                        </Grid>
                        {Object.keys(manifestItems).map((key, idx) =>
                            labelRow(key, manifestItems[key], handleCheck, handleItemCheck, idx === 0)
                        )}
                    </Grid>
                </ModalContent>
                <StickyModalActions border>
                    <SecondaryButton onClick={cancel}>Cancel</SecondaryButton>
                    <PrimaryButton disabled={loading.updateLoading || !anySelected || printing} onClick={printSelected}>
                        Print Selected
                    </PrimaryButton>
                </StickyModalActions>
            </NavResponsiveModal>
        </>
    );
};

export default PrintLabelsModal;
